import { useEffect, useState } from 'react'
import {Briefcase, Search, ChevronLeft, ChevronRight, MapPin, Trash2} from 'lucide-react'
import AdminLayout from '@/components/AdminLayout'
import { lumi } from '@/lib/lumi'

const PAGE_SIZE = 15

interface Vacancy {
  _id: string
  title?: string
  companyName?: string
  companyUserId?: string
  status?: string
  city?: string
  state?: string
  modality?: string
  salaryMin?: number
  salaryMax?: number
  contractType?: string
  industry?: string
  createdAt?: string
}

const STATUS_META: Record<string, { label: string; color: string }> = {
  publicada: { label: 'Publicada', color: 'bg-emerald-100 text-emerald-700' },
  pausada: { label: 'Pausada', color: 'bg-amber-100 text-amber-700' },
  cerrada: { label: 'Cerrada', color: 'bg-slate-100 text-slate-500' },
  borrador: { label: 'Borrador', color: 'bg-blue-100 text-blue-600' },
}

function AdminVacantes() {
  const [vacancies, setVacancies] = useState<Vacancy[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('todos')
  const [page, setPage] = useState(0)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    const fetchVacancies = async () => {
      setLoading(true)
      try {
        const filter: Record<string, unknown> = {}
        if (filterStatus !== 'todos') filter.status = filterStatus
        if (search) filter.$or = [{ title: { $regex: search, $options: 'i' } }, { companyName: { $regex: search, $options: 'i' } }]
        const res = await lumi.entities.vacancies.list({
          filter,
          sort: { createdAt: -1 },
          limit: PAGE_SIZE,
          skip: page * PAGE_SIZE,
        })
        setVacancies(res.list as Vacancy[])
        setTotal(res.total ?? res.list.length)
      } catch (e) {
        console.error('[AdminVacantes] error:', e)
      } finally {
        setLoading(false)
      }
    }
    fetchVacancies()
  }, [search, filterStatus, page, reloadKey])

  const handleDelete = async (v: Vacancy) => {
    if (!confirm(`¿Eliminar la vacante "${v.title ?? 'sin título'}"? Esta acción no se puede deshacer.`)) return
    setDeletingId(v._id)
    try {
      await lumi.entities.vacancies.delete(v._id)
      setReloadKey((k) => k + 1)
    } catch (e) {
      console.error('[AdminVacantes] delete error:', e)
      alert('No se pudo eliminar la vacante. Inténtalo de nuevo.')
    } finally {
      setDeletingId(null)
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <AdminLayout title="Vacantes" subtitle={`Supervisa todas las oportunidades publicadas en Beeznz Work · ${total} total`}>
          <div className="mb-5 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0) }}
                placeholder="Buscar por puesto o empresa…"
                className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
              />
            </div>
            <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(0) }}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-400">
              <option value="todos">Todos los estados</option>
              {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="space-y-2">{[...Array(8)].map((_, i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-slate-100" />)}</div>
          ) : vacancies.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-16 text-center">
              <Briefcase size={32} className="mb-3 text-slate-300" />
              <p className="font-semibold text-slate-600">Sin resultados</p>
            </div>
          ) : (
            <>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-100 bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Vacante</th>
                      <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 sm:table-cell">Empresa</th>
                      <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 md:table-cell">Ciudad</th>
                      <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 lg:table-cell">Modalidad</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Estado</th>
                      <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 lg:table-cell">Publicada</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {vacancies.map((v) => {
                      const meta = STATUS_META[v.status ?? ''] ?? { label: v.status ?? '—', color: 'bg-slate-100 text-slate-500' }
                      return (
                        <tr key={v._id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <p className="font-semibold text-slate-900">{v.title ?? '—'}</p>
                            {v.industry ? <p className="text-xs text-slate-400">{v.industry}</p> : null}
                          </td>
                          <td className="hidden px-4 py-3 text-slate-500 sm:table-cell">{v.companyName ?? '—'}</td>
                          <td className="hidden px-4 py-3 text-slate-500 md:table-cell">
                            {[v.city, v.state].filter(Boolean).length > 0 ? (
                              <span className="flex items-center gap-1"><MapPin size={12} />{[v.city, v.state].filter(Boolean).join(', ')}</span>
                            ) : '—'}
                          </td>
                          <td className="hidden px-4 py-3 text-slate-500 lg:table-cell capitalize">{v.modality ?? '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${meta.color}`}>{meta.label}</span>
                          </td>
                          <td className="hidden px-4 py-3 text-xs text-slate-400 lg:table-cell">{v.createdAt?.slice(0, 10) ?? '—'}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => handleDelete(v)}
                              disabled={deletingId === v._id}
                              title="Eliminar vacante"
                              className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                            >
                              <Trash2 size={13} /> {deletingId === v._id ? 'Eliminando…' : 'Eliminar'}
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 ? (
                <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
                  <p>Página {page + 1} de {totalPages}</p>
                  <div className="flex gap-2">
                    <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium transition hover:bg-slate-50 disabled:opacity-40"><ChevronLeft size={14} /> Anterior</button>
                    <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium transition hover:bg-slate-50 disabled:opacity-40">Siguiente <ChevronRight size={14} /></button>
                  </div>
                </div>
              ) : null}
            </>
          )}
    </AdminLayout>
  )
}

export default AdminVacantes
